import type { MarketHistoryPoint } from '../shared/analytics';

export type AnalyticsRange = '12h' | '24h' | '7d';

type AnalyticsBucket = {
  label: string;
  startBlock: bigint;
  endBlock: bigint;
  startTimeMs: number;
  endTimeMs: number;
  yesProbability: number;
  noProbability: number;
  yesBuyFlow: bigint;
  noBuyFlow: bigint;
  yesSellFlow: bigint;
  noSellFlow: bigint;
  buyFlow: bigint;
  sellFlow: bigint;
  netFlow: bigint;
};

type LabeledAnalyticsBucket = {
  label: string;
  startTimeMs: number;
  endTimeMs: number;
  yesProbability: number;
  noProbability: number;
  yesBuyFlow: bigint;
  noBuyFlow: bigint;
  yesSellFlow: bigint;
  noSellFlow: bigint;
  buyFlow: bigint;
  sellFlow: bigint;
  netFlow: bigint;
};

type BucketCacheEntry = {
  fingerprint: string;
  buckets: LabeledAnalyticsBucket[];
};

function analyticsBucketCount(range: AnalyticsRange, viewportWidth: number): number {
  if (viewportWidth <= 640) return range === '12h' ? 6 : range === '24h' ? 8 : 14;
  if (viewportWidth <= 900) return range === '12h' ? 8 : range === '24h' ? 10 : 18;
  return range === '12h' ? 10 : range === '24h' ? 14 : 26;
}

function analyticsHistoryFingerprint(history: MarketHistoryPoint[], bucketCount: number): string {
  const first = history[0];
  const last = history[history.length - 1];
  if (!first || !last) return `empty:${bucketCount}`;
  return [
    history.length,
    bucketCount,
    first.blockNumber.toString(),
    first.logIndex,
    first.timestampMs,
    last.blockNumber.toString(),
    last.logIndex,
    last.timestampMs,
  ].join(':');
}

function bucketHistory(history: MarketHistoryPoint[], maxBuckets = 16): AnalyticsBucket[] {
  if (history.length <= maxBuckets) {
    return history.map((point) => ({
      label: `#${point.blockNumber.toString()}`,
      startBlock: point.blockNumber,
      endBlock: point.blockNumber,
      startTimeMs: point.timestampMs,
      endTimeMs: point.timestampMs,
      yesProbability: point.yesProbability,
      noProbability: point.noProbability,
      yesBuyFlow: point.yesBuyFlow,
      noBuyFlow: point.noBuyFlow,
      yesSellFlow: point.yesSellFlow,
      noSellFlow: point.noSellFlow,
      buyFlow: point.buyFlow,
      sellFlow: point.sellFlow,
      netFlow: point.netFlow,
    }));
  }

  const chunkSize = Math.ceil(history.length / maxBuckets);
  const buckets: AnalyticsBucket[] = [];

  for (let start = 0; start < history.length; start += chunkSize) {
    const slice = history.slice(start, start + chunkSize);
    if (slice.length === 0) continue;
    const yesBuyFlow = slice.reduce((acc, point) => acc + point.yesBuyFlow, 0n);
    const noBuyFlow = slice.reduce((acc, point) => acc + point.noBuyFlow, 0n);
    const yesSellFlow = slice.reduce((acc, point) => acc + point.yesSellFlow, 0n);
    const noSellFlow = slice.reduce((acc, point) => acc + point.noSellFlow, 0n);
    const buyFlow = slice.reduce((acc, point) => acc + point.buyFlow, 0n);
    const sellFlow = slice.reduce((acc, point) => acc + point.sellFlow, 0n);
    const netFlow = slice.reduce((acc, point) => acc + point.netFlow, 0n);
    const first = slice[0];
    const last = slice[slice.length - 1];
    buckets.push({
      label: `#${first.blockNumber.toString()}-${last.blockNumber.toString()}`,
      startBlock: first.blockNumber,
      endBlock: last.blockNumber,
      startTimeMs: first.timestampMs,
      endTimeMs: last.timestampMs,
      // Use end-of-bucket probability so trend reflects actual market path.
      yesProbability: last.yesProbability,
      noProbability: last.noProbability,
      yesBuyFlow,
      noBuyFlow,
      yesSellFlow,
      noSellFlow,
      buyFlow,
      sellFlow,
      netFlow,
    });
  }

  return buckets;
}

async function applyBucketTimeLabels(
  series: AnalyticsBucket[],
  resolveBlockTimeLabel: (blockNumber: bigint) => Promise<string>,
): Promise<LabeledAnalyticsBucket[]> {
  const out: LabeledAnalyticsBucket[] = [];
  for (const point of series) {
    const [startLabel, endLabel] = await Promise.all([
      resolveBlockTimeLabel(point.startBlock),
      resolveBlockTimeLabel(point.endBlock),
    ]);
    out.push({
      label: point.startBlock === point.endBlock ? startLabel : `${startLabel} - ${endLabel}`,
      startTimeMs: point.startTimeMs,
      endTimeMs: point.endTimeMs,
      yesProbability: point.yesProbability,
      noProbability: point.noProbability,
      yesBuyFlow: point.yesBuyFlow,
      noBuyFlow: point.noBuyFlow,
      yesSellFlow: point.yesSellFlow,
      noSellFlow: point.noSellFlow,
      buyFlow: point.buyFlow,
      sellFlow: point.sellFlow,
      netFlow: point.netFlow,
    });
  }
  return out;
}

export function createAnalyticsBucketsService(params: {
  resolveBlockTimeLabel: (blockNumber: bigint) => Promise<string>;
  getViewportWidth?: () => number;
}) {
  const cache = new Map<AnalyticsRange, BucketCacheEntry>();
  const getViewportWidth = params.getViewportWidth ?? (() => 1280);

  return {
    async getLabeledBucketsForRange(range: AnalyticsRange, visibleHistory: MarketHistoryPoint[]): Promise<LabeledAnalyticsBucket[]> {
      const bucketCount = analyticsBucketCount(range, getViewportWidth());
      const fingerprint = analyticsHistoryFingerprint(visibleHistory, bucketCount);
      const cached = cache.get(range);
      if (cached?.fingerprint === fingerprint) {
        return cached.buckets;
      }

      const rawBuckets = bucketHistory(visibleHistory, bucketCount);
      const buckets = await applyBucketTimeLabels(rawBuckets, params.resolveBlockTimeLabel);
      cache.set(range, { fingerprint, buckets });
      return buckets;
    },
  };
}
